import { keepPreviousData, useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,} from "@tanstack/react-query";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";
import type {
  AlertAckRequest,
  AlertAckResult,
  AlertChannelInput,
  AlertChannelPage,
  AlertChannelResult,
  AlertPage,
  AlertRuleInput,
  AlertRulePage,
  AlertRuleResult,
  AlertState,
  SilenceInput,
  SilencePage,
  SilenceRemoved,
  SilenceResult,
} from "./vocab.ts";

/**
 * The reads AND writes for the Alerts screen (task 11.13; design 11 operator-API Alerts row, 12.2
 * Alerts row). All shapes come from `@oathera/contract/api` via `vocab.ts`; nothing is redefined.
 *
 * READS (role `viewer`), through `lib/api.ts` so a cold-core 503 surfaces as a `waking` ApiError:
 * - `GET /alerts` → {@link AlertPage}: alert HISTORY for the window (the live firing list prefers the
 *   stream view; see the screen). WINDOWED, so the window is resolved from the shared time-range and
 *   pausing freezes it exactly like the other windowed screens (bucketed "now" keeps the live key
 *   stable; frozen uses the exact window and stops advancing).
 * - `GET /alerts/rules` → {@link AlertRulePage}: the rules (built-in pack included). NOT windowed.
 * - `GET /alerts/silences` → {@link SilencePage}: the silences, carrying `max_silence_duration_s`
 *   (the invariant bound the create/change form reads). NOT windowed.
 * - `GET /alerts/channels` → {@link AlertChannelPage}: the channels with delivery status. NOT
 *   windowed.
 *
 * WRITES (role `operator`) via TanStack `useMutation`, each INVALIDATING the lists it affects so the
 * screen refetches the authoritative state on success. The mutations surface their `ApiError` to the
 * caller (a 403 → "the server did not permit this"; a 400/422 → the server's validation message, e.g.
 * the silence reason/bounded-duration rule). The query client already retries a mutation only when
 * the core is waking.
 */

/* ---- Query keys ----------------------------------------------------------------------------- */

const KEY = {
  alerts: (fromMs: number, toMs: number, states: readonly AlertState[]) =>
    ["alerts", fromMs, toMs, [...states].sort().join(",")] as const,
  rules: () => ["alerts", "rules"] as const,
  silences: () => ["alerts", "silences"] as const,
  channels: () => ["alerts", "channels"] as const,
};

/** The window a windowed read ran for, so the screen can label the snapshot honestly. */
export interface AlertsWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

/* ---- Reads ---------------------------------------------------------------------------------- */

export interface UseAlertHistoryOptions {
  /** States to include; the contract default is `pending,firing`. The history view asks for all. */
  readonly states?: readonly AlertState[];
  /** Page size; contract default 100. */
  readonly limit?: number;
  /** When false the read does not run (role below viewer). Defaults to true. */
  readonly enabled?: boolean;
}

export interface UseAlertHistoryResult {
  readonly query: UseQueryResult<AlertPage, ApiError>;
  readonly window: AlertsWindow;
}

/**
 * Fetch one page of alerts for the current window. The query key carries the window and the state
 * set so it refetches when either changes; `staleTime`/`refetchInterval` match the other windowed
 * screens (live tolerates slight staleness and polls; frozen never goes stale and does not poll).
 */
export function useAlertHistory(options: UseAlertHistoryOptions = {}): UseAlertHistoryResult {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  const window: AlertsWindow = { fromMs: resolved.fromMs, toMs: resolved.toMs };

  const states = options.states ?? ALL_STATES;
  const limit = options.limit ?? 100;
  const enabled = options.enabled ?? true;

  const query = useQuery<AlertPage, ApiError>({
    queryKey: KEY.alerts(window.fromMs, window.toMs, states),
    queryFn: ({ signal }) =>
      apiFetch<AlertPage>("/alerts", {
        query: {
          state: states.join(","),
          from: window.fromMs,
          to: window.toMs,
          limit,
        },
        signal,
      }),
    enabled,
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

const ALL_STATES: readonly AlertState[] = ["pending", "firing", "resolved"];

/** Fetch the alert rules (viewer). Not windowed; mutations invalidate it. */
export function useAlertRules(enabled = true): UseQueryResult<AlertRulePage, ApiError> {
  return useQuery<AlertRulePage, ApiError>({
    queryKey: KEY.rules(),
    queryFn: ({ signal }) => apiFetch<AlertRulePage>("/alerts/rules", { signal }),
    enabled,
    staleTime: 30_000,
  });
}

/** Fetch the silences (viewer). Carries `max_silence_duration_s` for the invariant form. */
export function useSilences(enabled = true): UseQueryResult<SilencePage, ApiError> {
  return useQuery<SilencePage, ApiError>({
    queryKey: KEY.silences(),
    queryFn: ({ signal }) => apiFetch<SilencePage>("/alerts/silences", { signal }),
    enabled,
    staleTime: 30_000,
  });
}

/** Fetch the notification channels (viewer) with delivery status. */
export function useAlertChannels(enabled = true): UseQueryResult<AlertChannelPage, ApiError> {
  return useQuery<AlertChannelPage, ApiError>({
    queryKey: KEY.channels(),
    queryFn: ({ signal }) => apiFetch<AlertChannelPage>("/alerts/channels", { signal }),
    enabled,
    staleTime: 30_000,
  });
}

/* ---- Writes (operator) ---------------------------------------------------------------------- */

/** Acknowledge an alert. On success invalidate every alerts list so the ack overlay shows. */
export function useAcknowledgeAlert(): UseMutationResult<
  AlertAckResult,
  ApiError,
  { readonly id: string; readonly body: AlertAckRequest }
> {
  const qc = useQueryClient();
  return useMutation<AlertAckResult, ApiError, { id: string; body: AlertAckRequest }>({
    mutationFn: ({ id, body }) =>
      apiFetch<AlertAckResult>(`/alerts/${encodeURIComponent(id)}/ack`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["alerts"], exact: false });
    },
  });
}

/** Create an alert rule. On success invalidate the rules list. */
export function useCreateAlertRule(): UseMutationResult<AlertRuleResult, ApiError, AlertRuleInput> {
  const qc = useQueryClient();
  return useMutation<AlertRuleResult, ApiError, AlertRuleInput>({
    mutationFn: (body) =>
      apiFetch<AlertRuleResult>("/alerts/rules", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.rules() });
    },
  });
}

/** Replace an alert rule. On success invalidate the rules list (and alerts, since tuning re-evaluates). */
export function useUpdateAlertRule(): UseMutationResult<
  AlertRuleResult,
  ApiError,
  { readonly id: string; readonly body: AlertRuleInput }
> {
  const qc = useQueryClient();
  return useMutation<AlertRuleResult, ApiError, { id: string; body: AlertRuleInput }>({
    mutationFn: ({ id, body }) =>
      apiFetch<AlertRuleResult>(`/alerts/rules/${encodeURIComponent(id)}`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.rules() });
      void qc.invalidateQueries({ queryKey: ["alerts"], exact: false });
    },
  });
}

/** Create a silence. On success invalidate the silences list and the alerts (silenced_by changes). */
export function useCreateSilence(): UseMutationResult<SilenceResult, ApiError, SilenceInput> {
  const qc = useQueryClient();
  return useMutation<SilenceResult, ApiError, SilenceInput>({
    mutationFn: (body) =>
      apiFetch<SilenceResult>("/alerts/silences", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.silences() });
      void qc.invalidateQueries({ queryKey: ["alerts"], exact: false });
    },
  });
}

/** Replace a silence. On success invalidate the silences list and the alerts. */
export function useUpdateSilence(): UseMutationResult<
  SilenceResult,
  ApiError,
  { readonly id: string; readonly body: SilenceInput }
> {
  const qc = useQueryClient();
  return useMutation<SilenceResult, ApiError, { id: string; body: SilenceInput }>({
    mutationFn: ({ id, body }) =>
      apiFetch<SilenceResult>(`/alerts/silences/${encodeURIComponent(id)}`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.silences() });
      void qc.invalidateQueries({ queryKey: ["alerts"], exact: false });
    },
  });
}

/**
 * Remove a silence. The removal reason goes in the `reason` QUERY param (the contract models the
 * delete this way, and the response is 200 with an audit receipt, not 204). On success invalidate
 * the silences list and the alerts.
 */
export function useDeleteSilence(): UseMutationResult<
  SilenceRemoved,
  ApiError,
  { readonly id: string; readonly reason: string }
> {
  const qc = useQueryClient();
  return useMutation<SilenceRemoved, ApiError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) =>
      apiFetch<SilenceRemoved>(`/alerts/silences/${encodeURIComponent(id)}`, {
        method: "DELETE",
        query: { reason },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.silences() });
      void qc.invalidateQueries({ queryKey: ["alerts"], exact: false });
    },
  });
}

/** Create a notification channel. On success invalidate the channels list. */
export function useCreateAlertChannel(): UseMutationResult<
  AlertChannelResult,
  ApiError,
  AlertChannelInput
> {
  const qc = useQueryClient();
  return useMutation<AlertChannelResult, ApiError, AlertChannelInput>({
    mutationFn: (body) =>
      apiFetch<AlertChannelResult>("/alerts/channels", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.channels() });
    },
  });
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
